import { type FC, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, SelectTrigger } from "@similarweb/ui-components";
import { Flex, TruncatedText } from "@similarweb/ui-components/foundations";

import { LEAD_SCORE_REFRESH_DATA_INTERVAL_OPTIONS } from "../../../constants";
import type { CrmEnrichmentI18nKeys } from "../../../types/translations";
import type { LeadScoreRefreshDataInterval } from "../../../types/types";

type Props = {
    selectedLeadScoreRefreshDataInterval: LeadScoreRefreshDataInterval | "";
    translationKeys: CrmEnrichmentI18nKeys;
    onSelectLeadScoreRefreshDataInterval?: (interval: LeadScoreRefreshDataInterval | "") => void;
};

const LeadScoreRefreshDataIntervalDropdown: FC<Props> = ({
    selectedLeadScoreRefreshDataInterval,
    onSelectLeadScoreRefreshDataInterval,
    translationKeys,
}) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (id: LeadScoreRefreshDataInterval): void => {
        setIsOpen(false);
        onSelectLeadScoreRefreshDataInterval?.(id);
    };

    const buttonLabel = selectedLeadScoreRefreshDataInterval
        ? translate(translationKeys.leadScoreRefreshDataInterval(selectedLeadScoreRefreshDataInterval))
        : translate("si.crm-enrichment.job-wizard.settings.lead-score-refresh.placeholder");

    return (
        <Combobox
            data-automation="crm-enrichment-lead-score-refresh-dropdown"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
        >
            <Combobox.Trigger>
                <SelectTrigger>
                    <Flex
                        gap="8px"
                        padding="0 12px 0 16px"
                        alignItems="center"
                    >
                        <TruncatedText
                            fontVariant="listItem1"
                            width="120px"
                            textAlign="start"
                        >
                            {buttonLabel}
                        </TruncatedText>
                    </Flex>
                </SelectTrigger>
            </Combobox.Trigger>

            <Combobox.Content>
                {LEAD_SCORE_REFRESH_DATA_INTERVAL_OPTIONS.map((option) => (
                    <Combobox.Option
                        key={option}
                        id={option}
                        textValue={translate(translationKeys.leadScoreRefreshDataInterval(option))}
                        isSelected={option === selectedLeadScoreRefreshDataInterval}
                        onClick={() => handleSelect(option)}
                    >
                        <Combobox.OptionLabel>
                            {translate(translationKeys.leadScoreRefreshDataInterval(option))}
                        </Combobox.OptionLabel>
                    </Combobox.Option>
                ))}
            </Combobox.Content>
        </Combobox>
    );
};

export default LeadScoreRefreshDataIntervalDropdown;
